import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../../../../../config.json";
import axios from "axios";
import "../../../../i18n";
import { useTranslation } from "react-i18next";
import { fetchRestoDataByUser } from "../../../../services/restaurantService";
import QrDesignCanvas, { type QrDesignCanvasHandle } from "./QrDesignCanvas";
import QrPresetLayer from "./QrPresetLayer";
import {
  DEFAULT_QR_PLACEMENT,
  QR_PRESET_TEMPLATES,
  clampPlacement,
  type QrDesignMode,
  type QrPlacement,
  type QrPresetId,
} from "./qrCodeTemplates";
import {
  buildQrPreviewDataUrl,
  fetchTemplateAspectRatio,
  getImageDimensions,
  isAcceptedTemplateFile,
  placementToFormFields,
  readFileAsDataUrl,
  renderPdfFirstPageDataUrl,
  triggerPdfDownload,
} from "./qrCodeDesignerUtils";
import "./QrCodeDesigner.css";
import { markOnboardingQrGenerated } from "../../../../utils/onboardingTutorialStorage";

/** Ensures a valid #RRGGBB for <input type="color" /> (browser requirement). */
function normalizeHexForColorInput(hex: string | null | undefined, fallback = "#F47F72") {
  if (hex == null || typeof hex !== "string") return fallback;
  let h = hex.trim();
  if (!h.startsWith("#")) h = `#${h}`;
  if (/^#[0-9A-F]{3}$/i.test(h)) {
    const r = h[1];
    const g = h[2];
    const b = h[3];
    h = `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9A-F]{6}$/i.test(h)) return h.toUpperCase();
  return fallback;
}

const GenerateQRcode = () => {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLocalUrl, setPdfLocalUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<"single" | "multiple">("single");
  const [showTableNumber, setShowTableNumber] = useState(true);
  const [tableNb, setTableNb] = useState<string>("");
  const [startTable, setStartTable] = useState<string>("");
  const [endTable, setEndTable] = useState<string>("");

  const [designMode, setDesignMode] = useState<QrDesignMode>("preset");
  const [presetId, setPresetId] = useState<QrPresetId>("classic");
  const [placement, setPlacement] = useState<QrPlacement>(DEFAULT_QR_PLACEMENT);
  const [selectedTemplate, setSelectedTemplate] = useState<File | null>(null);
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState<string | null>(null);
  const [templateIsPdf, setTemplateIsPdf] = useState(false);
  const [canvasAspectRatio, setCanvasAspectRatio] = useState(210 / 297);
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<QrDesignCanvasHandle>(null);
  const [restaurantData, setRestaurantData] = useState<any>(null);

  const domaineName = config.client;
  const resto = restaurantData?.restaurant ?? restaurantData;

  const safeMenuColors = resto?.menuColors || {
    buttonColor: "#f47f72",
    tableQRCode: "#f47f72",
  };

  const activePreset = useMemo(
    () => QR_PRESET_TEMPLATES.find((item) => item.id === presetId) || QR_PRESET_TEMPLATES[0],
    [presetId]
  );

  useEffect(() => {
    const loadRestoData = async () => {
      try {
        const data = await fetchRestoDataByUser();
        setRestaurantData(data);
      } catch (err) {
        console.error("Erreur lors de la récupération du restaurant :", err);
        setError(t("errorFetchRestoData") || "Impossible de récupérer le restaurant.");
      }
    };
    loadRestoData();
  }, [t]);

  const computedLinks = useMemo(() => {
    if (!resto?.country || !resto?.city || !resto?.nameRes) return [];

    const cleanCountry = resto.country.toLowerCase().replace(/\s+/g, "-");
    const cleanCity = resto.city.toLowerCase().replace(/\s+/g, "-");
    const cleanName = resto.nameRes.toLowerCase().replace(/\s+/g, "-");

    const buildUrl = (table: number | null) => {
      const tablePath = table ? `/${table}` : "";
      return `${domaineName}/restaurant/${cleanCountry}/${cleanCity}/${cleanName}${tablePath}`;
    };

    if (!showTableNumber) {
      return [{ table: null, url: buildUrl(null) }];
    }

    const MAX_LINKS = 20;
    const links: Array<{ table: number | null; url: string }> = [];

    if (mode === "single") {
      const tableNumber = parseInt(tableNb, 10);
      if (!isNaN(tableNumber) && tableNumber > 0) {
        links.push({ table: tableNumber, url: buildUrl(tableNumber) });
      } else {
        links.push({ table: null, url: buildUrl(null) });
      }
      return links;
    }

    const start = parseInt(startTable, 10);
    const end = parseInt(endTable, 10);
    if (isNaN(start) || isNaN(end) || start < 1 || end < 1 || start > end) {
      return [{ table: null, url: buildUrl(null) }];
    }

    for (let table = start; table <= end; table++) {
      if (links.length >= MAX_LINKS) break;
      links.push({ table, url: buildUrl(table) });
    }
    return links;
  }, [resto, domaineName, showTableNumber, mode, tableNb, startTable, endTable]);

  const previewLink = computedLinks[0];

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!previewLink?.url) {
        setQrPreviewUrl(null);
        return;
      }
      try {
        const dataUrl = await buildQrPreviewDataUrl(
          previewLink.url,
          showTableNumber ? previewLink.table : null,
          safeMenuColors.tableQRCode
        );
        if (!cancelled) setQrPreviewUrl(dataUrl);
      } catch (previewError) {
        console.error(previewError);
        if (!cancelled) setQrPreviewUrl(null);
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [previewLink, safeMenuColors.tableQRCode, showTableNumber]);

  useEffect(() => {
    return () => {
      if (templatePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(templatePreviewUrl);
      }
    };
  }, [templatePreviewUrl]);

  const handleTemplateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    if (!isAcceptedTemplateFile(file)) {
      setError(t("qrTemplateFileInvalid"));
      setSelectedTemplate(null);
      setTemplatePreviewUrl(null);
      setTemplateIsPdf(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setError(null);
    setSelectedTemplate(file);
    setDesignMode("custom");

    if (file.type === "application/pdf") {
      try {
        const rendered = await renderPdfFirstPageDataUrl(file);
        setCanvasAspectRatio(rendered.width / rendered.height);
        setTemplatePreviewUrl(rendered.dataUrl);
        setTemplateIsPdf(true);
      } catch (pdfPreviewError) {
        console.warn("PDF preview render failed, using API dimensions", pdfPreviewError);
        try {
          const token = localStorage.getItem("tokenLogin");
          const ratio = await fetchTemplateAspectRatio(file, config.dev, token);
          setCanvasAspectRatio(ratio);
        } catch {
          setCanvasAspectRatio(210 / 297);
        }
        const blobUrl = URL.createObjectURL(file);
        setTemplatePreviewUrl(blobUrl);
        setTemplateIsPdf(true);
      }
      return;
    }

    const dataUrl = await readFileAsDataUrl(file);
    setTemplatePreviewUrl(dataUrl);
    setTemplateIsPdf(false);
    try {
      const dims = await getImageDimensions(dataUrl);
      setCanvasAspectRatio(dims.width / dims.height);
    } catch {
      setCanvasAspectRatio(210 / 297);
    }
  };

  const handleDeleteTemplate = () => {
    if (templatePreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(templatePreviewUrl);
    }
    setSelectedTemplate(null);
    setTemplatePreviewUrl(null);
    setTemplateIsPdf(false);
    setCanvasAspectRatio(210 / 297);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selectPreset = (id: QrPresetId) => {
    setDesignMode("preset");
    setCanvasAspectRatio(210 / 297);
    setPresetId(id);
    const preset = QR_PRESET_TEMPLATES.find((item) => item.id === id);
    if (preset) setPlacement(preset.defaultPlacement);
  };

  const handleGenerateQRcode = async () => {
    setLoading(true);
    setError(null);
    setPdfUrl(null);
    setPdfLocalUrl(null);
    setPdfFilename(null);

    try {
      const token = localStorage.getItem("tokenLogin");
      const formData = new FormData();

      if (designMode === "custom" && selectedTemplate) {
        formData.append("file", selectedTemplate);
        formData.append(
          "templateType",
          selectedTemplate.type === "application/pdf" ? "pdf" : "image"
        );
      } else {
        formData.append("templateType", "preset");
        formData.append("presetId", presetId);
      }

      const measuredPlacement =
        canvasRef.current?.getMeasuredPlacement() ??
        clampPlacement(placement, canvasAspectRatio);
      const placementFields = placementToFormFields(measuredPlacement);
      formData.append("qrLeftPercent", placementFields.qrLeftPercent);
      formData.append("qrTopPercent", placementFields.qrTopPercent);
      formData.append("qrSizePercent", placementFields.qrSizePercent);

      if (safeMenuColors.tableQRCode) {
        formData.append("tableQRCodeColor", safeMenuColors.tableQRCode);
      }

      formData.append("domaineName", domaineName);
      formData.append("showTableNumber", showTableNumber ? "true" : "false");

      if (showTableNumber) {
        if (mode === "single") {
          const tableNumber = parseInt(tableNb, 10);
          if (!isNaN(tableNumber)) {
            formData.append("tableNb", tableNumber.toString());
          }
        } else {
          const start = parseInt(startTable, 10);
          const end = parseInt(endTable, 10);

          if (isNaN(start) || isNaN(end) || start < 1 || end < 1 || start > end) {
            setError(t("invalidRange") || "Plage de tables invalide (from ≤ to).");
            setLoading(false);
            return;
          }

          formData.append("range[start]", start.toString());
          formData.append("range[end]", end.toString());
          formData.append("rangeStart", start.toString());
          formData.append("rangeEnd", end.toString());
        }
      }

      const response = await axios.post(
        `${config.dev}/v2/restaurant/generateQRcode`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const pdfMeta = {
          pdfUrl: response.data.pdfUrl || null,
          pdfLocalUrl: response.data.pdfLocalUrl || null,
          pdfFilename: response.data.pdfFilename || null,
        };
        setPdfUrl(pdfMeta.pdfUrl || pdfMeta.pdfLocalUrl);
        setPdfLocalUrl(pdfMeta.pdfLocalUrl);
        setPdfFilename(pdfMeta.pdfFilename);
        markOnboardingQrGenerated();
        window.dispatchEvent(new CustomEvent("onboarding-tour:progress"));
        try {
          await triggerPdfDownload(pdfMeta, `qrcode-${Date.now()}.pdf`);
        } catch (downloadErr) {
          console.error("QR PDF download failed:", downloadErr);
          setError(
            t("errorDownloadQR") ||
              "QR generated, but PDF download failed. Use the download button below."
          );
        }
      }
    } catch (err) {
      console.error("Erreur:", err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      setError(
        apiMessage ||
          t("errorGenerateQR") ||
          "Erreur lors de la génération du QR code."
      );
    } finally {
      setLoading(false);
    }
  };

  const canvasBackgroundClass =
    designMode === "preset" ? activePreset.previewClass : "";
  const restaurantName = resto?.nameRes || "";

  return (
    <div className="qr-generator-page" data-tour="tour-page-qrcode">
      <div className="qr-generator-shell">
        <div className="qr-generator-header">
          <h2>{t("generateQRTitle")}</h2>
          <p>{t("generateQRSubtitle")}</p>
        </div>

        <div className="qr-generator-grid">
          <div className="qr-generator-panel">
            <h3 className="qr-generator-panel__title">{t("qrDesignCanvasTitle")}</h3>

            <div className="qr-design-mode-tabs">
              <button
                type="button"
                className={`qr-design-mode-tab${designMode === "preset" ? " is-active" : ""}`}
                onClick={() => setDesignMode("preset")}
                disabled={loading}
              >
                <strong>{t("qrDesignPresetMode")}</strong>
                <span>{t("qrDesignPresetModeDesc")}</span>
              </button>
              <button
                type="button"
                className={`qr-design-mode-tab${designMode === "custom" ? " is-active" : ""}`}
                onClick={() => setDesignMode("custom")}
                disabled={loading}
              >
                <strong>{t("qrDesignCustomMode")}</strong>
                <span>{t("qrDesignCustomModeDesc")}</span>
              </button>
            </div>

            {designMode === "preset" ? (
              <div className="qr-template-grid">
                {QR_PRESET_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={`qr-template-card${presetId === template.id ? " is-active" : ""}`}
                    onClick={() => selectPreset(template.id)}
                    disabled={loading}
                  >
                    <div className="qr-template-preview">
                      <QrPresetLayer
                        presetId={template.id}
                        restaurantName={restaurantName}
                        compact
                      />
                    </div>
                    <span className="qr-template-card__label">{t(template.labelKey)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="qr-upload-box">
                <label htmlFor="qr-template-file">{t("qrDesignUploadLabel")}</label>
                <input
                  id="qr-template-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  onChange={handleTemplateChange}
                  disabled={loading}
                />
                <p className="qr-upload-box__note">{t("qrDesignUploadHint")}</p>
                {selectedTemplate ? (
                  <div className="qr-upload-box__name">
                    <span>{selectedTemplate.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleDeleteTemplate}
                      disabled={loading}
                    >
                      {t("remove")}
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            <QrDesignCanvas
              ref={canvasRef}
              backgroundUrl={
                designMode === "custom" && templatePreviewUrl ? templatePreviewUrl : null
              }
              backgroundClassName={designMode === "preset" ? canvasBackgroundClass : ""}
              presetId={designMode === "preset" ? presetId : null}
              restaurantName={restaurantName}
              qrPreviewUrl={qrPreviewUrl}
              placement={placement}
              onPlacementChange={setPlacement}
              disabled={loading}
              aspectRatio={canvasAspectRatio}
            />
          </div>

          <div className="qr-generator-panel">
            <h3 className="qr-generator-panel__title">{t("qrDesignSettingsTitle")}</h3>

            <div className="qr-generator-form-group">
              <label>{t("qrTableNumberDisplayLabel")}</label>
              <div className="qr-generator-radio-row">
                <label>
                  <input
                    type="radio"
                    value="show"
                    checked={showTableNumber}
                    onChange={() => setShowTableNumber(true)}
                    disabled={loading}
                  />{" "}
                  {t("qrTableNumberShow")}
                </label>
                <label>
                  <input
                    type="radio"
                    value="hide"
                    checked={!showTableNumber}
                    onChange={() => setShowTableNumber(false)}
                    disabled={loading}
                  />{" "}
                  {t("qrTableNumberHide")}
                </label>
              </div>
            </div>

            {showTableNumber ? (
              <div className="qr-generator-form-group">
                <label htmlFor="qr-table-nb-color">{t("tableNbColor")}</label>
                <div className="qr-generator-color-row">
                  <input
                    id="qr-table-nb-color"
                    type="color"
                    className="qr-generator-color-picker"
                    aria-label={t("tableNbColor")}
                    value={normalizeHexForColorInput(safeMenuColors.tableQRCode)}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setRestaurantData((prev: any) => {
                        if (!prev) return prev;
                        const menuColors = {
                          ...(prev.restaurant?.menuColors || prev.menuColors || safeMenuColors),
                          tableQRCode: value,
                        };
                        if (prev.restaurant) {
                          return {
                            ...prev,
                            restaurant: { ...prev.restaurant, menuColors },
                          };
                        }
                        return { ...prev, menuColors };
                      });
                    }}
                    disabled={loading}
                  />
                  <input
                    type="text"
                    className="qr-generator-color-hex"
                    value={safeMenuColors.tableQRCode}
                    placeholder="#F47F72"
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      if (/^#([0-9A-F]{0,6})$/i.test(value) || value === "") {
                        setRestaurantData((prev: any) => {
                          if (!prev) return prev;
                          const menuColors = {
                            ...(prev.restaurant?.menuColors || prev.menuColors || safeMenuColors),
                            tableQRCode: value,
                          };
                          if (prev.restaurant) {
                            return {
                              ...prev,
                              restaurant: { ...prev.restaurant, menuColors },
                            };
                          }
                          return { ...prev, menuColors };
                        });
                      }
                    }}
                    disabled={loading}
                    spellCheck={false}
                  />
                  <div
                    className="qr-generator-color-swatch"
                    style={{
                      backgroundColor: /^#[0-9A-F]{6}$/i.test(safeMenuColors.tableQRCode)
                        ? safeMenuColors.tableQRCode
                        : "transparent",
                    }}
                    title={safeMenuColors.tableQRCode}
                  />
                </div>
              </div>
            ) : null}

            {showTableNumber ? (
              <>
                <div className="qr-generator-form-group">
                  <label>{t("qrDesignTableModeLabel")}</label>
                  <div className="qr-generator-radio-row">
                    <label>
                      <input
                        type="radio"
                        value="single"
                        checked={mode === "single"}
                        onChange={() => setMode("single")}
                        disabled={loading}
                      />{" "}
                      {t("singleQR")}
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="multiple"
                        checked={mode === "multiple"}
                        onChange={() => setMode("multiple")}
                        disabled={loading}
                      />{" "}
                      {t("multipleQR")}
                    </label>
                  </div>
                </div>

                <div className="qr-generator-form-group">
                  {mode === "single" ? (
                    <>
                      <label htmlFor="table-nb">{t("tableNb")}</label>
                      <input
                        id="table-nb"
                        name="tableNb"
                        value={tableNb}
                        onChange={(e) => setTableNb(e.target.value)}
                        type="number"
                        disabled={loading}
                        placeholder={t("tableNb")}
                      />
                    </>
                  ) : (
                    <div className="qr-generator-inline">
                      <div>
                        <label htmlFor="start-table">{t("from")}</label>
                        <input
                          id="start-table"
                          value={startTable}
                          onChange={(e) => setStartTable(e.target.value)}
                          type="number"
                          disabled={loading}
                          placeholder={t("starTableNb")}
                        />
                      </div>
                      <div>
                        <label htmlFor="end-table">{t("to")}</label>
                        <input
                          id="end-table"
                          value={endTable}
                          onChange={(e) => setEndTable(e.target.value)}
                          type="number"
                          disabled={loading}
                          placeholder={t("endTableNb")}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}

            <div className="qr-generator-actions">
              <p className="qr-generator-actions__hint">{t("qrDesignDownloadHint")}</p>
              <button
                type="button"
                className="btn-generate"
                onClick={handleGenerateQRcode}
                disabled={loading || (designMode === "custom" && !selectedTemplate)}
              >
                {loading ? t("generating") : t("qrDesignDownloadPdf")}
              </button>
            </div>

            {error ? <p className="qr-generator-error">{error}</p> : null}

            {computedLinks.length > 0 ? (
              <div className="qr-generator-links">
                <div className="qr-generator-links__title">
                  {t("qrCode")} : {t("qrDesignOpenLink")}
                </div>
                <div className="qr-generator-links__grid">
                  {computedLinks.map((item) => (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: safeMenuColors.buttonColor || "#fc8575",
                      }}
                    >
                      {item.table ? `${t("tableNb")} ${item.table}` : t("qrDesignRestaurantLink")}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {pdfUrl ? (
              <div className="qr-generator-download">
                <p className="qr-generator-download__title">{t("qrDesignPdfReady")}</p>
                <button
                  type="button"
                  className="btn btn-primary qr-generator-download__btn"
                  onClick={() =>
                    void triggerPdfDownload(
                      { pdfUrl, pdfLocalUrl, pdfFilename },
                      `qrcode-${Date.now()}.pdf`
                    ).catch((downloadErr) => {
                      console.error("QR PDF download failed:", downloadErr);
                      setError(
                        t("errorDownloadQR") ||
                          "PDF download failed. Please try again."
                      );
                    })
                  }
                >
                  <i className="fa fa-download me-2" />
                  {t("downloadPdf")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateQRcode;
